
import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from '@/components/ui/progress';
import { Calendar, Euro, FileText, AlertCircle, Info } from 'lucide-react';
import { intervalToDuration, formatDuration } from 'date-fns';
import { fr } from 'date-fns/locale';

const projectTypes = [
    { value: "expansion_capacity", label: "Expansion de capacité", description: "Augmentation de la capacité de production" },
    { value: "equipment", label: "Équipement industriel", description: "Acquisition d'équipements spécialisés" },
    { value: "innovation", label: "Innovation produit", description: "Développement de nouveaux produits" },
    { value: "quality_systems", label: "Systèmes qualité", description: "Amélioration des systèmes qualité" },
    { value: "regulatory_compliance", label: "Conformité réglementaire", description: "Mise en conformité réglementaire" },
    { value: "maintenance_preventive", label: "Maintenance préventive", description: "Programmes de maintenance" },
    { value: "continuous_improvement", label: "Amélioration continue", description: "Optimisation des processus" },
    { value: "infrastructure", label: "Infrastructure et utilités", description: "Infrastructure et utilités" },
    { value: "digitalization", label: "Digitalisation", description: "Transformation numérique" }
];

const priorityLevels = [
    { value: "low", label: "Faible", color: "bg-gray-200 text-gray-700", indicator: "bg-gray-400" },
    { value: "medium", label: "Normale", color: "bg-blue-200 text-blue-700", indicator: "bg-blue-500" },
    { value: "high", label: "Haute", color: "bg-orange-200 text-orange-700", indicator: "bg-orange-500" },
    { value: "urgent", label: "Critique", color: "bg-red-200 text-red-700", indicator: "bg-red-500" }
];

// @ts-nocheck
export default function ProjectForm({ isOpen, onClose, onSubmit, subsidiaries, departments, project, users }) {
    const [currentTab, setCurrentTab] = useState("basic");
    const [formProgress, setFormProgress] = useState(0);

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
        reset
    } = useForm({
        defaultValues: project || {
            name: '',
            subsidiary_id: '',
            department_id: '',
            type: 'equipment',
            priority: 'medium',
            total_budget: '',
            currency: 'EUR',
            expected_roi: '',
            start_date: '',
            end_date: '',
            description: '',
            business_justification: '',
            expected_benefits: '',
            project_manager_id: ''
        }
    });

    const selectedSubsidiary = watch("subsidiary_id");
    const watchedFields = watch();
    const startDate = watch("start_date");
    const endDate = watch("end_date");

    // Filter departments based on selected subsidiary using subsidiary_number
    const filteredDepartments = React.useMemo(() => {
        if (!selectedSubsidiary) return [];

        const subsidiary = subsidiaries.find(s => s.id === selectedSubsidiary);
        if (!subsidiary || !subsidiary.subsidiary_number) return [];

        const filtered = departments.filter(d => d.subsidiary_number === subsidiary.subsidiary_number);
        return filtered;
    }, [selectedSubsidiary, subsidiaries, departments]);

    // Reset department when subsidiary changes
    useEffect(() => {
        if (selectedSubsidiary) {
            setValue("department_id", "");
        }
    }, [selectedSubsidiary, setValue]);

    // Reset form when project changes
    React.useEffect(() => {
        if (project) {
            reset({
                name: project.name || '',
                subsidiary_id: project.subsidiary_id || '',
                department_id: project.department_id || '',
                type: project.type || 'equipment',
                priority: project.priority || 'medium',
                total_budget: project.total_budget || '',
                currency: project.currency || 'EUR',
                expected_roi: project.expected_roi || '',
                start_date: project.start_date || '',
                end_date: project.end_date || '',
                description: project.description || '',
                business_justification: project.business_justification || '',
                expected_benefits: project.expected_benefits || '',
                project_manager_id: project.project_manager_id || ''
            });
        }
    }, [project, reset]);

    // Calculate form completion progress
    React.useEffect(() => {
        const requiredFields = ['name', 'subsidiary_id', 'department_id', 'total_budget'];
        const optionalFields = ['type', 'priority', 'description', 'start_date', 'end_date', 'project_manager_id'];

        const requiredComplete = requiredFields.filter(field => watchedFields[field]).length;
        const optionalComplete = optionalFields.filter(field => watchedFields[field]).length;

        const progress = ((requiredComplete * 15) + (optionalComplete * 8));
        setFormProgress(Math.min(progress, 100));
    }, [watchedFields]);

    const calculatedDuration = useMemo(() => {
        if (!startDate || !endDate) return null;
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (end < start) return "Date de fin doit être après la date de début.";

        const duration = intervalToDuration({ start, end });

        // Calculate total months
        const totalMonths = (duration.years || 0) * 12 + (duration.months || 0);
        const days = duration.days || 0;

        if (totalMonths > 12) {
            const years = Math.floor(totalMonths / 12);
            const remainingMonths = totalMonths % 12;
            if (remainingMonths === 0 && days === 0) {
                return `${years} année${years > 1 ? 's' : ''}`;
            } else if (remainingMonths === 0 && days > 0) {
                return `${years} année${years > 1 ? 's' : ''} et ${days} jour${days > 1 ? 's' : ''}`;
            } else if (days === 0) {
                return `${years} année${years > 1 ? 's' : ''} et ${remainingMonths} mois`;
            } else {
                return `${years} année${years > 1 ? 's' : ''}, ${remainingMonths} mois et ${days} jour${days > 1 ? 's' : ''}`;
            }
        } else {
            if (totalMonths === 0 && days > 0) {
                return `${days} jour${days > 1 ? 's' : ''}`;
            } else if (days === 0) {
                return `${totalMonths} mois`;
            } else {
                return `${totalMonths} mois et ${days} jour${days > 1 ? 's' : ''}`;
            }
        }
    }, [startDate, endDate]);

    const handleFormSubmit = (data) => {
        const selectedSub = subsidiaries.find(s => s.id === data.subsidiary_id);
        const selectedDept = departments.find(d => d.id === data.department_id);

        const submitData = {
            ...data,
            subsidiary_number: selectedSub?.subsidiary_number,
            department_number: selectedDept?.subsidiary_number
        };

        onSubmit(submitData);
        reset();
        setCurrentTab("basic");
        setFormProgress(0);
    };

    const handleClose = () => {
        if (formProgress > 20) {
            if (window.confirm("Des modifications non sauvegardées seront perdues. Continuer ?")) {
                reset();
                setCurrentTab("basic");
                setFormProgress(0);
                onClose();
            }
        } else {
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-auto">
                <DialogHeader className="space-y-3">
                    <DialogTitle className="text-2xl font-bold text-gray-900">
                        {project ? 'Modifier le projet' : 'Nouveau Projet CAPEX'}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Remplissez les informations ci-dessous pour {project ? 'modifier' : 'créer'} le projet.
                    </DialogDescription>

                    <div className="space-y-2">
                        <div className="flex justify-between text-sm text-gray-600">
                            <span>Progression du formulaire</span>
                            <span>{formProgress}%</span>
                        </div>
                        <Progress value={formProgress} className="h-2" />
                    </div>
                </DialogHeader>

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
                        <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="basic" className="gap-2">
                                <FileText className="w-4 h-4" />
                                Informations
                            </TabsTrigger>
                            <TabsTrigger value="financial" className="gap-2">
                                <Euro className="w-4 h-4" />
                                Financier
                            </TabsTrigger>
                            <TabsTrigger value="timeline" className="gap-2">
                                <Calendar className="w-4 h-4" />
                                Planning
                            </TabsTrigger>
                            <TabsTrigger value="description" className="gap-2">
                                <Info className="w-4 h-4" />
                                Détails
                            </TabsTrigger>
                        </TabsList>

                        {/* Basic Information Tab */}
                        <TabsContent value="basic" className="space-y-6 mt-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="flex items-center gap-2">
                                        Nom du projet <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="name"
                                        {...register("name", {
                                            required: "Le nom est requis",
                                            minLength: { value: 5, message: "Minimum 5 caractères" }
                                        })}
                                        className={errors.name ? "border-red-500" : ""}
                                        placeholder="Ex: Expansion Ligne Yaourts - Phase 2"
                                    />
                                    {errors.name && (
                                        <p className="text-red-500 text-sm flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            {errors.name.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Code projet <span className="text-xs text-gray-500">(généré automatiquement)</span>
                                    </Label>
                                    <Input
                                        value={`CAPEX-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`}
                                        disabled
                                        className="bg-gray-50"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Filiale <span className="text-red-500">*</span>
                                    </Label>
                                    <Controller
                                        control={control}
                                        name="subsidiary_id"
                                        rules={{ required: "La filiale est requise" }}
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger className={errors.subsidiary_id ? "border-red-500" : ""}>
                                                    <SelectValue placeholder="Sélectionnez une filiale..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {subsidiaries.map(s => (
                                                        <SelectItem key={s.id} value={s.id}>
                                                            <div className="flex items-center gap-2">
                                                                <div
                                                                    className="w-3 h-3 rounded-full"
                                                                    style={{backgroundColor: s.color}}
                                                                />
                                                                {s.name}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {errors.subsidiary_id && (
                                        <p className="text-red-500 text-sm flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            {errors.subsidiary_id.message}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        Département <span className="text-red-500">*</span>
                                    </Label>
                                    <Controller
                                        control={control}
                                        name="department_id"
                                        rules={{ required: "Le département est requis" }}
                                        render={({ field }) => (
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value}
                                                disabled={!selectedSubsidiary}
                                            >
                                                <SelectTrigger className={errors.department_id ? "border-red-500" : ""}>
                                                    <SelectValue placeholder={!selectedSubsidiary ? "Sélectionnez d'abord une filiale" : "Sélectionnez un département..."} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {filteredDepartments.map(d => (
                                                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                    {filteredDepartments.length === 0 && selectedSubsidiary && (
                                        <p className="text-amber-600 text-sm">Aucun département trouvé pour cette filiale</p>
                                    )}
                                    {errors.department_id && (
                                        <p className="text-red-500 text-sm flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            {errors.department_id.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label>Type d'investissement</Label>
                                    <Controller
                                        control={control}
                                        name="type"
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Type de projet" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {projectTypes.map(type => (
                                                        <SelectItem key={type.value} value={type.value}>
                                                            <div>
                                                                <div className="font-medium">{type.label}</div>
                                                                <div className="text-xs text-gray-500">{type.description}</div>
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Priorité</Label>
                                    <Controller
                                        control={control}
                                        name="priority"
                                        render={({ field }) => (
                                            <Select onValueChange={field.onChange} value={field.value}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Priorité" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {priorityLevels.map(priority => (
                                                        <SelectItem key={priority.value} value={priority.value}>
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-3 h-3 rounded-full ${priority.indicator}`} />
                                                                {priority.label}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        {/* Financial Tab */}
                        <TabsContent value="financial" className="space-y-6 mt-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-1 space-y-2">
                                    <Label htmlFor="currency">Devise du projet</Label>
                                    <Controller
                                        name="currency"
                                        control={control}
                                        render={({ field }) => (
                                            <Select value={field.value} onValueChange={field.onChange}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="MAD">Dirham marocain (DH)</SelectItem>
                                                    <SelectItem value="EUR">Euro (€)</SelectItem>
                                                    <SelectItem value="USD">Dollar américain ($)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>

                                <div className="md:col-span-1 space-y-2">
                                    <Label htmlFor="total_budget">Budget estimé global</Label>
                                    <Input
                                        id="total_budget"
                                        {...register("total_budget", {
                                            required: "Le budget est obligatoire",
                                            valueAsNumber: true,
                                            min: { value: 0, message: "Le budget doit être positif" }
                                        })}
                                        type="number"
                                        step="0.01"
                                        className={errors.total_budget ? "border-red-500" : ""}
                                        placeholder="0.00"
                                    />
                                    {errors.total_budget && (
                                        <p className="text-red-500 text-sm mt-1">{errors.total_budget.message}</p>
                                    )}
                                </div>

                                <div className="md:col-span-1 space-y-2">
                                    <Label htmlFor="expected_roi">ROI attendu (%)</Label>
                                    <Input
                                        id="expected_roi"
                                        {...register("expected_roi", { valueAsNumber: true })}
                                        type="number"
                                        step="0.1"
                                        placeholder="0.0"
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        {/* Timeline Tab */}
                        <TabsContent value="timeline" className="space-y-6 mt-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label>Date de début</Label>
                                    <Input
                                        type="date"
                                        {...register("start_date")}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Date de fin estimée</Label>
                                    <Input
                                        type="date"
                                        {...register("end_date")}
                                        min={startDate || new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            </div>

                            {calculatedDuration && (
                                <div className="space-y-2">
                                    <Label>Durée estimée</Label>
                                    <div className="p-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-5 h-5 text-teal-600" />
                                            <span className="text-lg font-semibold text-teal-800">
                        {calculatedDuration}
                      </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </TabsContent>

                        {/* Description Tab */}
                        <TabsContent value="description" className="space-y-6 mt-6">
                            <div className="space-y-2">
                                <Label>Chef de projet</Label>
                                <Controller
                                    control={control}
                                    name="project_manager_id"
                                    render={({ field }) => (
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Sélectionner un chef de projet (optionnel)..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {users?.map(user => (
                                                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Description détaillée</Label>
                                <Textarea
                                    {...register("description")}
                                    rows={4}
                                    placeholder="Décrivez en détail le projet, ses objectifs et sa mise en œuvre..."
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Justification métier</Label>
                                <Textarea
                                    {...register("business_justification")}
                                    rows={3}
                                    placeholder="Expliquez la nécessité métier et les enjeux stratégiques..."
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Bénéfices attendus</Label>
                                <Textarea
                                    {...register("expected_benefits")}
                                    rows={3}
                                    placeholder="• Augmentation de la capacité de 30%&#10;• Réduction des coûts de 15%&#10;• Amélioration de la qualité..."
                                />
                            </div>
                        </TabsContent>
                    </Tabs>

                    <DialogFooter className="flex-col sm:flex-row gap-3 pt-6 border-t">
                        <div className="flex gap-2 sm:order-2">
                            <Button type="button" variant="outline" onClick={handleClose}>
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                className="bg-teal-600 hover:bg-teal-700"
                                disabled={formProgress < 50}
                            >
                                {project ? 'Mettre à jour' : 'Créer le projet'}
                            </Button>
                        </div>

                        <div className="text-sm text-gray-500 sm:order-1">
                            {formProgress < 50 && (
                                <span className="flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Complétez au moins 50% du formulaire pour continuer
                </span>
                            )}
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
